import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, IndianRupee, Package, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const STAGES: Array<{ key: string; label: string }> = [
    { key: "confirmed", label: "Accept" },
    { key: "packing", label: "Packed" },
    { key: "out_for_delivery", label: "Out for delivery" },
    { key: "delivered", label: "Delivered" },
];

export default function AdminOrderDetail() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const { data: order, isLoading } = useQuery({
        queryKey: ["admin", "order", id],
        queryFn: () => adminApi.getOrder(id),
        enabled: Boolean(id),
    });

    const statusMut = useMutation({
        mutationFn: (status: string) => adminApi.updateOrderStatus(id, status),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "order", id] });
            qc.invalidateQueries({ queryKey: ["admin", "orders"] });
            toast.success("Order status updated");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't update")),
    });

    const cancelMut = useMutation({
        mutationFn: () => {
            const reason = window.prompt("Cancellation reason (optional):", "");
            if (reason === null) return Promise.reject(new Error("cancelled"));
            return adminApi.updateOrderStatus(id, "cancelled", reason || undefined);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "order", id] });
            qc.invalidateQueries({ queryKey: ["admin", "orders"] });
            toast.success("Order cancelled");
        },
        onError: (err) => {
            if ((err as Error)?.message === "cancelled") return;
            toast.error(formatApiError(err, "Couldn't cancel"));
        },
    });

    const refundMut = useMutation({
        mutationFn: () => adminApi.refundOrder(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "order", id] });
            toast.success("Refund initiated");
        },
        onError: (err) => toast.error(formatApiError(err, "Refund failed")),
    });

    const groupStatusMut = useMutation({
        mutationFn: (status: "accepted" | "preparing" | "ready") => {
            const sid = order?.scoped_to_store as string | undefined;
            if (!sid) return Promise.reject(new Error("Not scoped to a store"));
            return adminApi.updateStoreGroupStatus(id, sid, status);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "order", id] });
            qc.invalidateQueries({ queryKey: ["admin", "orders"] });
            toast.success("Store status updated");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't update store status")),
    });

    if (isLoading || !order) {
        return (
            <div className="rounded-2xl border border-border/70 bg-card p-6 text-sm text-muted-foreground">
                Loading order…
            </div>
        );
    }

    const canRefund = order.payment?.status === "paid";
    const canCancel = !["out_for_delivery", "delivered", "cancelled"].includes(order.status);

    return (
        <div data-testid="admin-order-detail-page" className="space-y-6">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold sm:text-3xl">{order.number}</h1>
                    <p className="text-sm text-muted-foreground">
                        Placed {new Date(order.placed_at).toLocaleString("en-IN")}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {STAGES.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => statusMut.mutate(s.key)}
                            disabled={order.status === s.key || statusMut.isPending}
                            data-testid={`admin-order-stage-${s.key}`}
                            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                order.status === s.key
                                    ? "bg-brand text-white shadow-brand"
                                    : "border border-border hover:bg-muted"
                            } disabled:opacity-60`}
                        >
                            {s.label}
                        </button>
                    ))}
                    {canCancel ? (
                        <button
                            onClick={() => cancelMut.mutate()}
                            disabled={cancelMut.isPending}
                            data-testid="admin-order-cancel"
                            className="rounded-full border border-error/50 bg-error/10 px-3 py-1.5 text-xs font-semibold text-error hover:bg-error/20"
                        >
                            Cancel order
                        </button>
                    ) : null}
                    {canRefund ? (
                        <button
                            onClick={() => refundMut.mutate()}
                            disabled={refundMut.isPending}
                            data-testid="admin-order-refund"
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Refund
                        </button>
                    ) : null}
                </div>
            </div>

            {order.scoped_to_store ? (
                <section
                    data-testid="store-order-action-strip"
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand/40 bg-brand/5 p-4"
                >
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-brand">Your store</p>
                        <p className="text-sm text-muted-foreground">
                            Advance the fulfilment status for your items only. Cancellation stays with global admins.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {(["accepted", "preparing", "ready"] as const).map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => groupStatusMut.mutate(s)}
                                disabled={groupStatusMut.isPending}
                                data-testid={`store-group-${s}`}
                                className="rounded-full bg-brand px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-brand disabled:opacity-50"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </section>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-4">
                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-brand" />
                            <h3 className="text-lg font-bold">Items</h3>
                        </div>
                        <ul className="mt-4 divide-y divide-border/60">
                            {(order.items || []).map((it) => (
                                <li key={it.product_id} className="flex items-center gap-3 py-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-semibold">{it.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {it.weight} · {rupee(it.price)} × {it.quantity}
                                        </p>
                                    </div>
                                    <p className="font-semibold">{rupee(it.price * it.quantity)}</p>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <h3 className="text-lg font-bold">Delivery address</h3>
                        {order.address ? (
                            <div className="mt-3 text-sm">
                                <p className="font-semibold">
                                    {String(order.address.full_name || "")}
                                </p>
                                <p className="text-muted-foreground">
                                    {String(order.address.line1 || "")}
                                    {order.address.line2 ? `, ${order.address.line2}` : ""}
                                </p>
                                <p className="text-muted-foreground">
                                    {String(order.address.city || "")},{" "}
                                    {String(order.address.state || "")} - {String(order.address.pincode || "")}
                                </p>
                                <p className="text-muted-foreground">Phone: {String(order.address.phone || "")}</p>
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-muted-foreground">No address on file.</p>
                        )}
                    </section>
                </div>

                <div className="space-y-4">
                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <div className="flex items-center gap-2">
                            <IndianRupee className="h-4 w-4 text-brand" />
                            <h3 className="text-lg font-bold">Bill</h3>
                        </div>
                        <dl className="mt-3 space-y-1.5 text-sm">
                            {Object.entries(order.breakdown || {}).map(([k, v]) => (
                                <div key={k} className="flex justify-between">
                                    <dt className="text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                                    <dd className="font-semibold">
                                        {typeof v === "number" ? rupee(v) : String(v ?? "—")}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <h3 className="text-lg font-bold">Customer</h3>
                        {order.customer ? (
                            <div className="mt-2 text-sm">
                                <p className="font-semibold">{order.customer.full_name}</p>
                                <p className="text-muted-foreground">{order.customer.email}</p>
                                <p className="text-muted-foreground">{order.customer.phone}</p>
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-muted-foreground">Guest checkout</p>
                        )}
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <h3 className="text-lg font-bold">Payment</h3>
                        <p className="mt-1 text-sm">
                            <span className="font-semibold uppercase">{order.payment?.method}</span>
                            {" · "}
                            <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    order.payment?.status === "paid"
                                        ? "bg-emerald-500/10 text-emerald-700"
                                        : "bg-amber-500/10 text-amber-700"
                                }`}
                            >
                                {order.payment?.status}
                            </span>
                        </p>
                        {order.cancellation_reason ? (
                            <p className="mt-3 text-xs text-error">
                                Cancelled: {order.cancellation_reason}
                            </p>
                        ) : null}
                    </section>
                </div>
            </div>
        </div>
    );
}
